export type EventNameOption = { label: string; value: string; description?: string };

/**
 * Build event picker options: `|`, `+`, and `*` are wildcards in event name
 * matching, so a picked literal name stores them escaped — an event named
 * `checkout*sale` saved unescaped would match more events than the user
 * picked. Only picked options are escaped; typed input stays raw, since a
 * hand-typed wildcard is intentional.
 *
 * (On this surface the escaping is parity rather than protection: a
 * people-search `named_bitmap` name is a byte-exact index lookup with no
 * matcher, so an escaped pick is a dead reference matching nothing — which is
 * exactly what the Ember editor produces for the same pick, and parity wins
 * while both apps serve the page.)
 */
export function escapedEventNameOptions(eventNames: readonly string[]): EventNameOption[] {
  return eventNames.map((eventName) => ({
    label: eventName,
    value: eventName.replace(/([|+*])/g, String.raw`\$1`),
    description: /[|+*]/.test(eventName) ? 'Special characters will be escaped' : undefined,
  }));
}

/**
 * Merge a picker's local (environment-seeded) and remote (searched) event
 * options into one deduped, term-filtered list. The picker filters here
 * rather than in the combobox so stale remote results from a previous search
 * don't flash during the debounce window; local options come first, and
 * duplicates (case-insensitive on value) are dropped.
 */
export function mergeEventOptions(
  local: EventNameOption[],
  remote: EventNameOption[],
  term: string,
): EventNameOption[] {
  const needle = term.trim().toLowerCase();
  const matchesTerm = (option: EventNameOption) =>
    needle === '' || option.label.toLowerCase().includes(needle) || option.value.toLowerCase().includes(needle);

  const seen = new Set<string>();
  const merged: EventNameOption[] = [];
  for (const option of [...local.filter(matchesTerm), ...remote.filter(matchesTerm)]) {
    const key = option.value.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(option);
    }
  }
  return merged;
}
